// components/UserManagement.tsx
import React, { useState, useEffect } from 'react';
import { Users, Shield, Check, X, Edit2, Save, AlertCircle } from 'lucide-react';
import { supabaseService } from '../SupabaseService';

interface UserRole {
  id?: number;
  user_name: string;
  role: 'admin' | 'manager' | 'user';
  permissions: {
    can_access_maintenance: boolean;
    can_access_reports: boolean;
    can_manage_users: boolean;
  };
  updated_at?: string;
}

interface UserManagementProps {
  isOpen: boolean;
  onClose: () => void;
  currentUser: string;
}

const UserManagement: React.FC<UserManagementProps> = ({
  isOpen,
  onClose,
  currentUser
}) => {
  const [users, setUsers] = useState<UserRole[]>([]);
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [tempChanges, setTempChanges] = useState<UserRole | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadUsers();
    }
  }, [isOpen]);

  const loadUsers = async () => {
    setIsLoading(true);
    try {
      const userRoles = await supabaseService.getAllUserRoles();
      
      // Sørg for at alle kjente brukere er med
      const knownUsers = ['Tormod', 'Johan', 'Rose', 'Odd Arthur', 'Trond Øyvind', 'Robert'];
      const existingUsers = userRoles.map(u => u.user_name);
      
      for (const userName of knownUsers) {
        if (!existingUsers.includes(userName)) {
          userRoles.push({
            user_name: userName,
            role: 'user',
            permissions: {
              can_access_maintenance: false,
              can_access_reports: false,
              can_manage_users: false
            }
          });
        }
      }
      
      setUsers(userRoles.sort((a, b) => a.user_name.localeCompare(b.user_name)));
    } catch (error) {
      console.error('Feil ved lasting av brukere:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const startEditing = (user: UserRole) => {
    setEditingUser(user.user_name);
    setTempChanges({ ...user });
  };

  const cancelEditing = () => {
    setEditingUser(null);
    setTempChanges(null);
  };

  const saveChanges = async () => {
    if (!tempChanges) return;
    
    setIsSaving(true);
    try {
      const success = await supabaseService.updateUserRole(
        tempChanges.user_name,
        tempChanges.role,
        tempChanges.permissions
      );
      
      if (success) {
        await loadUsers();
        setEditingUser(null);
        setTempChanges(null);
      } else {
        alert('Kunne ikke oppdatere brukerrolle');
      }
    } catch (error) {
      console.error('Feil ved lagring:', error);
      alert('Feil ved lagring av endringer');
    } finally {
      setIsSaving(false);
    }
  };

  const updateRole = (role: 'admin' | 'manager' | 'user') => {
    if (!tempChanges) return;
    
    // Hvis admin, gi alle tillatelser
    if (role === 'admin') {
      setTempChanges({
        ...tempChanges,
        role,
        permissions: {
          can_access_maintenance: true,
          can_access_reports: true,
          can_manage_users: true
        }
      });
    } else {
      setTempChanges({
        ...tempChanges,
        role
      });
    }
  };

  const togglePermission = (permission: keyof UserRole['permissions']) => {
    if (!tempChanges) return;
    
    setTempChanges({
      ...tempChanges,
      permissions: {
        ...tempChanges.permissions,
        [permission]: !tempChanges.permissions[permission]
      }
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Users className="text-white" size={24} />
            <h3 className="text-xl font-semibold text-white">Brukeradministrasjon</h3>
          </div>
          <button onClick={onClose} className="text-white hover:text-gray-200">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Laster brukere...</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Info boks */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="text-blue-600 mt-0.5" size={18} />
                  <div className="text-sm text-blue-800">
                    <p className="font-semibold">Om roller og tillatelser:</p>
                    <ul className="mt-1 space-y-1">
                      <li>• <strong>Admin:</strong> Full tilgang til alt</li>
                      <li>• <strong>Manager:</strong> Kan få utvalgte tillatelser</li>
                      <li>• <strong>User:</strong> Standard bruker uten spesielle tillatelser</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Brukertabell */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Bruker</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Rolle</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Vedlikehold</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Rapporter</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Admin brukere</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-gray-900">Handlinger</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => {
                      const isEditing = editingUser === user.user_name;
                      const displayUser = isEditing && tempChanges ? tempChanges : user;
                      const isCurrentUser = user.user_name === currentUser;
                      
                      return (
                        <tr key={user.user_name} className={`border-b ${isCurrentUser ? 'bg-blue-50' : ''}`}>
                          <td className="px-4 py-3">
                            <div className="flex items-center space-x-2">
                              <Shield className={`h-4 w-4 ${
                                displayUser.role === 'admin' ? 'text-red-500' :
                                displayUser.role === 'manager' ? 'text-orange-500' :
                                'text-gray-400'
                              }`} />
                              <span className="font-medium text-gray-900">
                                {user.user_name}
                                {isCurrentUser && <span className="ml-2 text-xs text-blue-600">(deg)</span>}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {isEditing ? (
                              <select
                                value={displayUser.role}
                                onChange={(e) => updateRole(e.target.value as 'admin' | 'manager' | 'user')}
                                className="border border-gray-300 rounded px-2 py-1 text-sm"
                                disabled={isCurrentUser}
                              >
                                <option value="user">Bruker</option>
                                <option value="manager">Manager</option>
                                <option value="admin">Admin</option>
                              </select>
                            ) : (
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                displayUser.role === 'admin' ? 'bg-red-100 text-red-800' :
                                displayUser.role === 'manager' ? 'bg-orange-100 text-orange-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {displayUser.role === 'admin' ? 'Admin' :
                                 displayUser.role === 'manager' ? 'Manager' : 'Bruker'}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {isEditing && displayUser.role !== 'admin' ? (
                              <button
                                onClick={() => togglePermission('can_access_maintenance')}
                                className="p-1"
                                disabled={isCurrentUser}
                              >
                                {displayUser.permissions.can_access_maintenance ? 
                                  <Check className="h-5 w-5 text-green-600" /> : 
                                  <X className="h-5 w-5 text-gray-400" />
                                }
                              </button>
                            ) : (
                              displayUser.permissions.can_access_maintenance ? 
                                <Check className="h-5 w-5 text-green-600 mx-auto" /> : 
                                <X className="h-5 w-5 text-gray-400 mx-auto" />
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {isEditing && displayUser.role !== 'admin' ? (
                              <button
                                onClick={() => togglePermission('can_access_reports')}
                                className="p-1"
                                disabled={isCurrentUser}
                              >
                                {displayUser.permissions.can_access_reports ? 
                                  <Check className="h-5 w-5 text-green-600" /> : 
                                  <X className="h-5 w-5 text-gray-400" />
                                }
                              </button>
                            ) : (
                              displayUser.permissions.can_access_reports ? 
                                <Check className="h-5 w-5 text-green-600 mx-auto" /> : 
                                <X className="h-5 w-5 text-gray-400 mx-auto" />
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {isEditing && displayUser.role !== 'admin' ? (
                              <button
                                onClick={() => togglePermission('can_manage_users')}
                                className="p-1"
                                disabled={isCurrentUser}
                              >
                                {displayUser.permissions.can_manage_users ? 
                                  <Check className="h-5 w-5 text-green-600" /> : 
                                  <X className="h-5 w-5 text-gray-400" />
                                }
                              </button>
                            ) : (
                              displayUser.permissions.can_manage_users ? 
                                <Check className="h-5 w-5 text-green-600 mx-auto" /> : 
                                <X className="h-5 w-5 text-gray-400 mx-auto" />
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {isEditing ? (
                              <div className="flex items-center justify-end space-x-2">
                                <button
                                  onClick={saveChanges}
                                  disabled={isSaving}
                                  className="p-1 text-green-600 hover:text-green-700"
                                >
                                  <Save className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={cancelEditing}
                                  className="p-1 text-red-600 hover:text-red-700"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => startEditing(user)}
                                disabled={!currentUser || (currentUser !== 'Tormod' && isCurrentUser)}
                                className="p-1 text-blue-600 hover:text-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                title={isCurrentUser && currentUser !== 'Tormod' ? 'Du kan ikke endre din egen rolle' : 'Rediger'}
                              >
                                <Edit2 className="h-4 w-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Lukk
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;